import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Service } from '../../model/professional/service-model';
import { Clock, Calendar, Check } from 'lucide-react';

interface ServiceCardProps {
  service: Service;
  isSelected: boolean;
  onSelect: (service: Service) => void;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ service, isSelected, onSelect }) => {
  const defaultImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjE1MCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5NGEzYjgiPlNlcnZpY2U8L3RleHQ+PC9zdmc+';
  const serviceImage = service.image
    ? (service.imageVariants?.medium || service.image)
    : defaultImage;

  const [showFullDescription, setShowFullDescription] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const descriptionRef = React.useRef<HTMLParagraphElement | null>(null);

  useEffect(() => {
    if (descriptionRef.current) {
      setIsOverflowing(descriptionRef.current.scrollHeight > descriptionRef.current.clientHeight);
    }
  }, [descriptionRef]);

  return (
    <>
      {/* Layout Desktop - Card format */}
      <div className={`hidden md:block group relative bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col ${isSelected 
          ? 'ring-2 ring-primary' 
          : ''}`}
        onClick={() => onSelect(service)}
      >
        {isSelected && (
          <div className="absolute inset-0 bg-primary/5 z-0 border border-primary rounded-xl"></div>
        )}

        {/* Image avec clic pour prévisualisation */}
        <div className="relative w-full h-44 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent z-10"></div>
          <img 
            src={serviceImage}
            alt={service.name} 
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            onError={(e) => {
              e.currentTarget.src = defaultImage;
            }}
          />
          {isSelected && (
            <div className="absolute top-3 right-3 bg-primary text-white rounded-full p-1.5 shadow-lg z-20">
              <Check className="h-4 w-4" />
            </div>
          )}
          {/* Badge acompte si requis */}
          {service.depositRequired && service.depositType && service.depositValue && (
            <div className="absolute top-3 left-3 bg-blue-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow-md z-20">
              Acompte {service.depositType === 'percentage' ? `${service.depositValue}%` : `${service.depositValue}€`}
            </div>
          )}
          <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm text-primary font-bold px-3 py-1.5 rounded-full shadow-md z-20">
            {typeof service.price === 'number' ? service.price.toFixed(2) : service.price}€
          </div>
        </div>

        {/* Contenu */}
        <div className="p-5 flex-grow flex flex-col justify-between relative z-10">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">{service.name}</h3>

            {service.description && (
              <div>
                <p
                  ref={descriptionRef}
                  className="text-sm text-gray-600 mb-2 line-clamp-2"
                >
                  {service.description}
                </p>
                {isOverflowing && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowFullDescription(true);
                    }}
                    className="text-xs text-primary hover:underline"
                  >
                    Voir plus
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-3 mt-auto pt-3">
            {service.duration && (
              <div className="flex items-center text-gray-600 bg-gray-50 px-3 py-1.5 rounded-full">
                <Clock className="h-3.5 w-3.5 mr-1.5 text-primary" />
                <span className="text-xs font-medium">
                  {service.duration >= 60 
                    ? `${Math.floor(service.duration / 60)}h${service.duration % 60 > 0 ? service.duration % 60 : ''}` 
                    : `${service.duration} min`}
                </span>
              </div>
            )}

            <div className="flex items-center">
              <span className={`text-xs font-medium px-3 py-1.5 rounded-full flex items-center ${service.requiresBooking 
                ? 'bg-amber-50 text-amber-700' 
                : 'bg-emerald-50 text-emerald-700'}`}>
                <Calendar className="h-3.5 w-3.5 mr-1.5" />
                {service.requiresBooking ? 'Sur rendez-vous' : 'Sans rendez-vous'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Layout Mobile - List format */}
      <div className={`md:hidden bg-white rounded-xl border border-gray-100 mb-2 ${isSelected ? 'ring-2 ring-primary' : ''}`}
        onClick={() => onSelect(service)}
      >
        <div className="flex items-center p-4">
          {/* Image */}
          <div className="flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden">
            <img 
              src={serviceImage}
              alt={service.name} 
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.src = defaultImage;
              }}
            />
          </div>

          {/* Content */}
          <div className="flex-grow ml-4">
            <h3 className="text-base font-semibold text-gray-800">{service.name}</h3>
            
            {/* Description avec "Voir plus" pour mobile */}
            {service.description && (
              <div className="mt-1">
                <p className="text-xs text-gray-600 line-clamp-1">
                  {service.description}
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFullDescription(true);
                  }}
                  className="text-xs text-primary hover:underline"
                >
                  Voir plus
                </button>
              </div>
            )}
            
            {/* Duration and booking info */}
            <div className="mt-1 space-y-0.5">
              {service.duration && (
                <p className="text-sm text-gray-500">
                  {service.duration >= 60 
                    ? `${Math.floor(service.duration / 60)}h${service.duration % 60 > 0 ? service.duration % 60 : ''}` 
                    : `${service.duration} min`}
                </p>
              )}
              <p className="text-sm text-gray-500 font-semibold">
                {service.requiresBooking ? 'Sur rendez-vous' : 'Sans rendez-vous'}
              </p>
            </div>
          </div>

          {/* Price and Arrow */}
          <div className="flex-shrink-0 text-right">
            <div className="text-lg font-bold text-gray-800">
              {typeof service.price === 'number' ? service.price.toFixed(2) : service.price}€
            </div>
          </div>
        </div>
      </div>

      {/* Modale description complète - utilisation de createPortal pour la rendre directement dans le body */}
      {showFullDescription && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl relative">
            <h4 className="text-lg font-semibold mb-3">{service.name}</h4>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{service.description}</p>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setShowFullDescription(false);
              }} 
              className="mt-4 px-4 py-2 bg-primary text-white rounded-md text-sm"
            >
              Fermer
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ServiceCard;